import { useMemo, useState } from 'react';

import { useShowCauseW_Selector } from '@givebacks/api/core';
import { type Cart, CartItem, type ConsumerCheckoutCartsResponse } from '@givebacks/api/store';
import { Button, Collapse, Divider, Stack, Text } from 'ui';

import { centsToDollar } from '../../../utils/currency';
import { OrderLineItem } from '../../molecules/OrderLineItem/OrderLineItem';
import { OrderBlock } from '../OrderBlock/OrderBlock';
import { useCheckoutInfoContext } from './CheckoutInfo.context';

const getFirstCartItemFromFirstCart = (
  checkoutResponse: ConsumerCheckoutCartsResponse
): CartItem | undefined => {
  return checkoutResponse?.checkout.carts[0]?.cart_items?.[0];
};

const OrderTotalSummaryLineItemSubtotal = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const pledgeUnit = useMemo(() => {
    const firstCartItem = getFirstCartItemFromFirstCart(checkoutResponse);

    return firstCartItem?.store_item.units_of_activity_singular;
  }, [checkoutResponse]);

  const isPledge = checkoutResponse?.checkout.pledge_required;

  const subTotalAmount =
    isPledge && pledgeUnit
      ? `${centsToDollar(checkoutResponse?.checkout.subtotal || 0)} / ${pledgeUnit}`
      : centsToDollar(checkoutResponse?.checkout.subtotal || 0);

  return <OrderLineItem label="Subtotal" value={subTotalAmount} valueBold />;
};

const OrderTotalSummaryLineItemFees = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const isPledge = checkoutResponse?.checkout.pledge_required;

  const fee = isPledge
    ? 'Calculated after event'
    : centsToDollar(checkoutResponse?.checkout.fee ?? 0);

  if (checkoutResponse?.checkout.processing_fees == 'zero_fees') {
    return null;
  }

  return <OrderLineItem label="Fees" value={fee} valueBold />;
};

const OrderTotalSummaryLineItemLedgerAppliedAmount = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const ledgerAppliedAmount = checkoutResponse?.checkout.ledger_applied_amount;

  if (!ledgerAppliedAmount) return null;

  return (
    <OrderLineItem
      label="Rewards Applied"
      value={`-${centsToDollar(ledgerAppliedAmount)}`}
      valueBold
      valueColor="mint.8"
    />
  );
};

const OrderTotalSummaryLineItemTip = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const isPledge = checkoutResponse?.checkout.pledge_required;
  const isTipping = checkoutResponse?.checkout.tip !== undefined;

  if (!isTipping) return null;

  const centsTip = centsToDollar(checkoutResponse?.checkout.tip ?? 0);
  const percentTip = `${checkoutResponse?.checkout.tip_percent}%`;

  if (isPledge) {
    const pledgeTip = checkoutResponse.checkout?.tip_percent !== null ? percentTip : centsTip;
    return <OrderLineItem label="Tip" value={pledgeTip} valueBold />;
  }

  return <OrderLineItem label="Tip" value={centsTip} valueBold />;
};

const OrderTotalSummaryLineItemDiscount = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const discountsByName = useMemo(() => {
    const uniqueDiscounts = new Set(
      checkoutResponse?.checkout.discounts.map((discount) => discount.name)
    );
    return Array.from(uniqueDiscounts);
  }, [checkoutResponse]);

  const displayAmount = useMemo(() => {
    if (!checkoutResponse?.checkout.discount) return '';
    return `-${centsToDollar(checkoutResponse.checkout.discount)}`;
  }, [checkoutResponse]);

  if (!discountsByName.length) return null;

  return (
    <OrderLineItem.Discount
      couponCodes={discountsByName.map((discount) => discount)}
      discountAmount={displayAmount}
    />
  );
};

const OrderTotalSummaryLineItemTotal = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const isPledge = checkoutResponse?.checkout.pledge_required;

  const totalAmount = isPledge
    ? 'Calculated after event'
    : centsToDollar(checkoutResponse?.checkout.total_due ?? 0);

  return <OrderLineItem label="Total Due" value={totalAmount} labelBold valueBold />;
};

const OrderTotalSummary = () => {
  return (
    <OrderBlock.Container>
      <OrderTotalSummaryLineItemSubtotal />
      <OrderTotalSummaryLineItemDiscount />
      <OrderTotalSummaryLineItemFees />
      <OrderTotalSummaryLineItemTip />
      <OrderTotalSummaryLineItemLedgerAppliedAmount />
      <OrderTotalSummaryLineItemTotal />
    </OrderBlock.Container>
  );
};

const OrderBreakdownByStore = ({ cart }: { cart: Cart }) => {
  const feeGroups = useMemo(
    () =>
      cart?.fee_groups?.map((feeGroup) => {
        const isPledge = cart.status === 'pledge_required';
        return <OrderBlock key={feeGroup.type} feeGroup={feeGroup} isPledge={isPledge} />;
      }),
    [cart]
  );

  const { data: cause } = useShowCauseW_Selector({ uuid: cart.cause_id });
  return (
    <Stack gap="md">
      <Text fz="14" fw={600}>
        {cause?.name}
      </Text>
      <Stack gap="md">
        {feeGroups}
        <Divider />
      </Stack>
    </Stack>
  );
};

const OrderBreakdown = () => {
  const { checkoutResponse } = useCheckoutInfoContext();

  const carts = useMemo(() => checkoutResponse?.checkout.carts ?? [], [checkoutResponse]);

  return (
    <Stack gap="md">
      {carts.map((cart) => (
        <OrderBreakdownByStore key={cart.uuid} cart={cart} />
      ))}
    </Stack>
  );
};

const CheckoutInfo = () => {
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);

  return (
    <Stack gap="md">
      <Collapse in={isBreakdownExpanded}>
        <OrderBreakdown />
      </Collapse>

      <OrderTotalSummary />

      <Divider />

      <Button
        variant="subtle"
        size="xs"
        w="fit-content"
        mx="auto"
        onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
      >
        {isBreakdownExpanded ? 'Hide' : 'View'} Breakdown
      </Button>
    </Stack>
  );
};

export { CheckoutInfo };
